import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  CurrencyDollarIcon,
  TrashIcon,
  DocumentIcon,
  PencilIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { driversService } from '../services/logistics/drivers';
import { shipmentsService } from '../services/logistics/shipments';
import { clientsService } from '../services/logistics/clients';
import { vehiclesService } from '../services/logistics/vehicles';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { internalDriverPaymentsService } from '../services/logistics/internal-driver-payments';
import DriverStatementModal from '../components/drivers/DriverStatementModal';
import AuditHistoryButton from '../components/audit/AuditHistoryButton';
import { AUDIT_MODULES } from '../services/system/audit';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { citiesGrouped } from '../data/saudiCities';
import { formatCurrency } from '../utils/formatting';

const DriverDetail = () => {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.language || 'ar').toLowerCase().startsWith('ar');
  const routeArrow = isRtl ? '←' : '→';
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);

  // Ledger / payments
  const [driverPayments, setDriverPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    method: 'cash',
    notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [quickViewShipment, setQuickViewShipment] = useState(null);

  // Inline commission edit
  const [editingCommissionId, setEditingCommissionId] = useState(null);
  const [editingCommissionValue, setEditingCommissionValue] = useState('');
  const [savingCommission, setSavingCommission] = useState(false);

  // Edit driver modal
  const [vehicles, setVehicles] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingDriver, setSavingDriver] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    nickname: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    iqamahNumber: '',
    iqamahExpiry: '',
    healthInsuranceNumber: '',
    healthInsuranceExpiry: '',
    favoriteVehicleId: '',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoading(true);
        
        const [driverData, shipmentsData, clientsData, vehiclesData, driversData] = await Promise.all([
          driversService.getDriver(id),
          shipmentsService.getShipments(currentCompany.id),
          clientsService.getClients(currentCompany.id),
          vehiclesService.getVehicles(currentCompany.id),
          driversService.getDrivers(currentCompany.id)
        ]);
        setVehicles(vehiclesData);
        setAllDrivers(
          (driversData || []).slice().sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          )
        );

        if (driverData) {
          const driverShipments = shipmentsData.filter(s => s.driverId === id);
          setDriver(driverData);
          setShipments(driverShipments);
          setClients(clientsData);

          try {
            const payments = await internalDriverPaymentsService.getPaymentsForDriver(currentCompany.id, id);
            setDriverPayments(payments);
          } catch (err) {
            console.error('Error loading driver payments:', err);
          }
        }
      } catch (error) {
        console.error('Error fetching driver data:', error);
        toast.error('Failed to load driver details');
      } finally {
        setLoading(false);
      }
    };

    fetchDriverData();
  }, [id, currentCompany?.id]);

  const getCityName = (cityKey) => {
    if (!cityKey) return '';
    const lang = isRtl ? 'ar' : 'en';
    for (const [, cities] of Object.entries(citiesGrouped)) {
      const city = cities.find(c => c.value === cityKey || c.key === cityKey);
      if (city) {
        const label = city.label || city.name;
        if (label) return label[lang] || label.ar || label.en || cityKey;
      }
    }
    return cityKey;
  };

  // Helper function to format route display for new route system
  const formatRouteDisplay = (shipment) => {
    // If shipment has routeCities (new system), use that
    if (shipment.routeCities && shipment.routeCities.length > 0) {
      const validCities = shipment.routeCities.filter(city => city.city);
      
      if (validCities.length === 0) {
        return { origin: 'N/A', destination: 'N/A' };
      }
      
      if (validCities.length === 1) {
        const cityName = getCityName(validCities[0].city);
        return { origin: cityName, destination: cityName };
      }
      
      // Get origin (first city)
      const originCity = getCityName(validCities[0].city);
      
      // Get destination cities (excluding origin)
      const destinationCities = validCities.slice(1).map(city => getCityName(city.city));
      
      if (destinationCities.length === 0) {
        return { origin: originCity, destination: originCity };
      }
      
      if (destinationCities.length === 1) {
        return { origin: originCity, destination: destinationCities[0] };
      }
      
      // Multiple destination cities - format as "City1 - City2 - City3"
      // If there are 3 or more destination cities, exclude the first destination city
      let citiesToShow = destinationCities;
      if (destinationCities.length >= 3) {
        citiesToShow = destinationCities.slice(1); // Skip first destination city
      }
      
      const destinationDisplay = citiesToShow.join(' - ');
      
      return { origin: originCity, destination: destinationDisplay };
    }
    
    // Fallback to old system
    return {
      origin: getCityName(shipment.originCity) || shipment.origin || 'N/A',
      destination: getCityName(shipment.destinationCity) || shipment.destination || 'N/A'
    };
  };

  const getStopsCount = (shipment) => {
    if (!Array.isArray(shipment.routeCities)) return 0;
    return shipment.routeCities.reduce((sum, city) => {
      if (!city) return sum;
      const fromArray = Array.isArray(city.stops) ? city.stops.length : 0;
      const fromCount = Number(city.stopCount) || 0;
      return sum + Math.max(fromArray, fromCount);
    }, 0);
  };

  const getClientName = (shipment) => {
    // First try to get client name directly from shipment
    if (shipment.clientName) {
      return shipment.clientName;
    }
    
    // Then try to find client by ID in clients array
    if (shipment.clientId) {
      const client = clients.find(c => c.id === shipment.clientId);
      if (client?.name) {
        return client.name;
      }
    }
    
    // Fallback to Unknown Client
    return t('common.unknown_client', 'Unknown Client');
  };


  const reloadPayments = async () => {
    try {
      const payments = await internalDriverPaymentsService.getPaymentsForDriver(currentCompany.id, id);
      setDriverPayments(payments);
    } catch (err) {
      console.error('Error reloading payments:', err);
    }
  };

  const handleSavePayment = async () => {
    const amt = Number(paymentForm.amount);
    if (!amt || amt <= 0) {
      toast.error(t('drivers.invalid_amount', 'Please enter a valid amount'));
      return;
    }
    try {
      setSavingPayment(true);
      await internalDriverPaymentsService.addPayment({
        companyId: currentCompany.id,
        driverId: id,
        amount: amt,
        paymentDate: paymentForm.paymentDate,
        method: paymentForm.method,
        notes: paymentForm.notes
      });
      toast.success(t('drivers.payment_recorded', 'Payment recorded'));
      setShowPaymentModal(false);
      setPaymentForm({
        amount: '',
        paymentDate: new Date().toISOString().slice(0, 10),
        method: 'cash',
        notes: ''
      });
      await reloadPayments();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save payment');
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm(t('drivers.delete_payment_confirm', 'Delete this payment?'))) return;
    try {
      await internalDriverPaymentsService.deletePayment(paymentId);
      toast.success(t('drivers.payment_deleted', 'Payment deleted'));
      await reloadPayments();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete payment');
    }
  };

  const handleSaveCommission = async (shipmentId) => {
    const amt = Number(editingCommissionValue);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error(t('drivers.invalid_amount', 'Enter a valid amount'));
      return;
    }
    setSavingCommission(true);
    try {
      await shipmentsService.updateShipment(shipmentId, { commissionAmount: amt });

      const existingCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
      if (existingCosting?.id) {
        await shipmentCostingsService.updateCostingWithoutValidation(existingCosting.id, {
          commissionAmount: amt,
          commission: amt,
        });
      } else {
        await shipmentCostingsService.addCosting({
          shipmentId,
          companyId: currentCompany.id,
          driverId: id,
          commissionAmount: amt,
          commission: amt,
        });
      }

      setShipments(prev => prev.map(s => s.id === shipmentId ? { ...s, commissionAmount: amt } : s));
      setEditingCommissionId(null);
      setEditingCommissionValue('');
      toast.success(t('drivers.commission_saved', 'Commission saved'));
    } catch (err) {
      console.error('Failed to save commission:', err);
      toast.error(`${t('drivers.commission_save_failed', 'Failed to save commission')}: ${err?.message || err}`);
    } finally {
      setSavingCommission(false);
    }
  };

  const handleSaveDriver = async () => {
    if (!editForm.name || !editForm.phone) {
      toast.error(t('common.required_field', 'Name and phone are required'));
      return;
    }
    setSavingDriver(true);
    try {
      await driversService.updateDriver(id, {
        ...editForm,
        companyId: currentCompany.id
      });
      toast.success(t('drivers.driver_updated_success', 'Driver updated'));
      const updated = await driversService.getDriver(id);
      if (updated) setDriver(updated);
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
      toast.error(t('drivers.failed_to_save', 'Failed to save driver'));
    } finally {
      setSavingDriver(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const toMillis = (v) => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Date.parse(v);
      return Number.isNaN(n) ? 0 : n;
    }
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v.seconds) return v.seconds * 1000;
    return 0;
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('drivers.driver_not_found', 'Driver Not Found')}</h2>
          <button 
            onClick={() => navigate('/drivers')}
            className="btn btn-primary"
          >
            {t('drivers.back_to_drivers', 'Back to Drivers')}
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      {(() => {
        const currentIndex = allDrivers.findIndex(d => d.id === id);
        const prevDriver = currentIndex > 0 ? allDrivers[currentIndex - 1] : null;
        const nextDriver = currentIndex >= 0 && currentIndex < allDrivers.length - 1 ? allDrivers[currentIndex + 1] : null;
        return (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <button
            onClick={() => navigate('/drivers')}
            className="text-gray-500 hover:text-gray-700"
          >
            ← {t('common.back', 'Back')}
          </button>
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-2 py-1">
            <button
              onClick={() => prevDriver && navigate(`/drivers/${prevDriver.id}`)}
              disabled={!prevDriver}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm text-gray-700 hover:text-gray-900 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
              title={prevDriver ? prevDriver.name : t('drivers.no_previous', 'No previous driver')}
            >
              <ChevronLeftIcon className="h-4 w-4 rtl:hidden" />
              <ChevronRightIcon className="h-4 w-4 hidden rtl:block" />
              <span>{t('common.previous', 'Previous')}</span>
            </button>
            {allDrivers.length > 0 && currentIndex >= 0 && (
              <span className="text-xs font-medium text-gray-600 tabular-nums px-1 select-none">
                {t('drivers.driver_position', '{{current}} of {{total}}', {
                  current: currentIndex + 1,
                  total: allDrivers.length
                })}
              </span>
            )}
            <button
              onClick={() => nextDriver && navigate(`/drivers/${nextDriver.id}`)}
              disabled={!nextDriver}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm text-gray-700 hover:text-gray-900 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"
              title={nextDriver ? nextDriver.name : t('drivers.no_next', 'No next driver')}
            >
              <span>{t('common.next', 'Next')}</span>
              <ChevronRightIcon className="h-4 w-4 rtl:hidden" />
              <ChevronLeftIcon className="h-4 w-4 hidden rtl:block" />
            </button>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {driver.name}
              {driver.phone && (
                <span className="text-sm font-normal text-gray-500 ml-2 rtl:mr-2 rtl:ml-0">({driver.phone})</span>
              )}
              {driver.driverCode && (
                <span className="ml-2 rtl:mr-2 rtl:ml-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 align-middle">
                  {driver.driverCode}
                </span>
              )}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AuditHistoryButton module={AUDIT_MODULES.DRIVER} recordId={id} />
          <button
            onClick={() => {
              setEditForm({
                name: driver.name || '',
                nickname: driver.nickname || '',
                email: driver.email || '',
                phone: driver.phone || '',
                licenseNumber: driver.licenseNumber || '',
                licenseExpiry: driver.licenseExpiry || '',
                iqamahNumber: driver.iqamahNumber || '',
                iqamahExpiry: driver.iqamahExpiry || '',
                healthInsuranceNumber: driver.healthInsuranceNumber || '',
                healthInsuranceExpiry: driver.healthInsuranceExpiry || '',
                favoriteVehicleId: driver.favoriteVehicleId || '',
                status: driver.status || 'active',
                notes: driver.notes || ''
              });
              setShowEditModal(true);
            }}
            className="btn btn-secondary flex items-center gap-2"
          >
            <PencilIcon className="h-5 w-5" />
            <span>{t('common.edit', 'Edit')}</span>
          </button>
          <button
            onClick={() => setShowStatementModal(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>{t('drivers.statement', 'Statement')}</span>
          </button>
        </div>
      </div>
        );
      })()}

      {/* Driver Ledger Section */}
      {(() => {
        const dayKey = (ms) => {
          if (!ms) return 0;
          const d = new Date(ms);
          return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
        };
        const getShipmentDateMs = (s) =>
          toMillis(s.pickupDate) ||
          toMillis(s.shipmentDate) ||
          toMillis(s.createdAt) ||
          0;

        // Tracking numbers look like `${yymmdd}${seq3}`, where `seq3` is a
        // per-day counter assigned at create time. For legacy shipments
        // without `recordedAt`, this gives us a reliable within-day order.
        const trackingSeq = (s) => {
          const tn = String(s.trackingNumber || s.shipmentNumber || '');
          const m = tn.match(/(\d{3})$/);
          return m ? Number(m[1]) : 0;
        };

        const tripEntries = shipments.map(s => ({
          id: `trip-${s.id}`,
          shipmentId: s.id,
          type: 'trip',
          dateMs: getShipmentDateMs(s),
          createdMs: toMillis(s.recordedAt) || toMillis(s.createdAt),
          seq: trackingSeq(s),
          amount: Number(s.commissionAmount || 0),
          description: `${t('drivers.trip', 'Trip')} ${s.trackingNumber || s.shipmentNumber || s.id.slice(0, 6)}`,
          secondary: (() => {
            const route = formatRouteDisplay(s);
            const routeStr = route.origin && route.destination ? `${route.origin} ${routeArrow} ${route.destination}` : null;
            return [getClientName(s), routeStr].filter(Boolean).join(' • ');
          })(),
          stopsCount: getStopsCount(s)
        }));

        const paymentEntries = driverPayments.map(p => ({
          id: `pay-${p.id}`,
          paymentId: p.id,
          type: 'payment',
          dateMs: toMillis(p.paymentDate) || toMillis(p.createdAt),
          createdMs: toMillis(p.createdAt),
          seq: 0,
          amount: Number(p.amount) || 0,
          description: t('drivers.payment_received', 'Payment'),
          secondary: [p.method, p.notes].filter(Boolean).join(' • ')
        }));

        const ledger = [...tripEntries, ...paymentEntries].sort((a, b) => {
          // 1. Day of the displayed business date.
          const dayDiff = dayKey(a.dateMs) - dayKey(b.dateMs);
          if (dayDiff !== 0) return dayDiff;
          // 2. Real wall-clock creation time when both sides have it.
          const aT = a.createdMs || 0;
          const bT = b.createdMs || 0;
          if (aT && bT && aT !== bT) return aT - bT;
          // 3. Tie-break trip-vs-trip by tracking-number sequence (legacy
          //    shipments where `recordedAt` is missing still order correctly
          //    because the per-day counter increments on each create).
          if (a.type === 'trip' && b.type === 'trip' && a.seq !== b.seq) {
            return a.seq - b.seq;
          }
          // 4. When only one side has a real timestamp, the timestamped one
          //    wins (a payment with a real time is more authoritative than a
          //    legacy trip that only knows its business date).
          if (aT !== bT) return aT - bT;
          // 5. Final stable tiebreaker.
          return String(a.id).localeCompare(String(b.id));
        });

        let running = 0;
        const ledgerWithBalance = ledger.map(e => {
          running += e.type === 'trip' ? e.amount : -e.amount;
          return { ...e, balance: running };
        });

        const totalEarned = tripEntries.reduce((s, e) => s + e.amount, 0);
        const totalPaid = paymentEntries.reduce((s, e) => s + e.amount, 0);
        const balance = totalEarned - totalPaid;

        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {t('drivers.total_trip_amount', 'Total trip amount')}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalEarned)}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {t('drivers.total_paid', 'Total paid')}
                </p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalPaid)}</p>
              </div>
              <div className={`rounded-lg border p-4 ${balance > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
                <p className="text-xs uppercase tracking-wide text-gray-500">
                  {t('drivers.remaining_balance', 'Remaining balance')}
                </p>
                <p className={`text-2xl font-bold mt-1 ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                  {formatCurrency(balance)}
                </p>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('drivers.ledger', 'Ledger')}
                  </h3>
                  <button
                    onClick={() => {
                      setPaymentForm({
                        amount: '',
                        paymentDate: new Date().toISOString().slice(0, 10),
                        method: 'cash',
                        notes: ''
                      });
                      setShowPaymentModal(true);
                    }}
                    className="btn btn-primary btn-sm flex items-center"
                  >
                    <CurrencyDollarIcon className="h-4 w-4 me-1" />
                    {t('drivers.record_payment', 'Record Payment')}
                  </button>
                </div>
                {ledgerWithBalance.length === 0 ? (
                  <div className="text-center py-10">
                    <DocumentIcon className="mx-auto h-10 w-10 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      {t('drivers.ledger_empty', 'No trips or payments yet')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-1">
                    <table className="min-w-full">
                      <thead>
                        <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                          <th className="px-3 py-2 font-medium w-28">{t('common.date', 'Date')}</th>
                          <th className="px-3 py-2 font-medium">{t('drivers.description', 'Description')}</th>
                          <th className="px-3 py-2 font-medium w-32">{t('drivers.credit', 'Credit')}</th>
                          <th className="px-3 py-2 font-medium w-32">{t('drivers.debit', 'Debit')}</th>
                          <th className="px-3 py-2 font-medium w-32">{t('drivers.balance', 'Balance')}</th>
                          <th className="px-2 py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {ledgerWithBalance.map(entry => (
                          <tr
                            key={entry.id}
                            className={`text-sm ${entry.type === 'trip' ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50'}`}
                            onClick={() => {
                              if (entry.type === 'trip' && entry.shipmentId) {
                                const ship = shipments.find(s => s.id === entry.shipmentId);
                                if (ship) setQuickViewShipment(ship);
                              }
                            }}
                          >
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap tabular-nums">
                              {entry.dateMs ? new Date(entry.dateMs).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium shrink-0 ${
                                  entry.type === 'trip' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                                }`}>
                                  {entry.type === 'trip' ? t('drivers.trip', 'Trip') : t('drivers.payment', 'Payment')}
                                </span>
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 truncate flex items-center gap-2 flex-wrap">
                                    <span className="truncate">{entry.description}</span>
                                    {entry.type === 'trip' && entry.stopsCount > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-semibold bg-amber-100 text-amber-800 border border-amber-300 shrink-0">
                                        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z" clipRule="evenodd" />
                                        </svg>
                                        {t('drivers.locations', { count: entry.stopsCount, defaultValue: '{{count}} locations' })}
                                      </span>
                                    )}
                                  </div>
                                  {entry.secondary && (
                                    <div className="text-xs text-gray-500 truncate">{entry.secondary}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap tabular-nums" onClick={(e) => e.stopPropagation()}>
                              {entry.type === 'trip' ? (
                                editingCommissionId === entry.shipmentId ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      autoFocus
                                      value={editingCommissionValue}
                                      onChange={(e) => setEditingCommissionValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveCommission(entry.shipmentId);
                                        if (e.key === 'Escape') { setEditingCommissionId(null); setEditingCommissionValue(''); }
                                      }}
                                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                      disabled={savingCommission}
                                    />
                                    <button
                                      onClick={() => handleSaveCommission(entry.shipmentId)}
                                      className="text-green-600 hover:text-green-800 text-xs font-semibold"
                                      disabled={savingCommission}
                                    >
                                      {savingCommission ? '...' : t('common.save', 'Save')}
                                    </button>
                                    <button
                                      onClick={() => { setEditingCommissionId(null); setEditingCommissionValue(''); }}
                                      className="text-gray-500 hover:text-gray-700 text-xs"
                                      disabled={savingCommission}
                                    >
                                      {t('common.cancel', 'Cancel')}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setEditingCommissionId(entry.shipmentId);
                                      setEditingCommissionValue(entry.amount > 0 ? String(entry.amount) : '');
                                    }}
                                    className={`hover:underline ${entry.amount > 0 ? 'font-medium text-gray-900' : 'text-blue-600 italic text-xs'}`}
                                    title={t('drivers.set_commission', 'Set commission')}
                                  >
                                    {entry.amount > 0
                                      ? formatCurrency(entry.amount)
                                      : t('drivers.commission_pending', 'Commission pending')}
                                  </button>
                                )
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                              {entry.type === 'payment' ? (
                                <span className="font-medium text-green-600">{formatCurrency(entry.amount)}</span>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap tabular-nums font-semibold text-gray-900">
                              {formatCurrency(entry.balance)}
                            </td>
                            <td className="px-2 py-2">
                              {entry.type === 'payment' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePayment(entry.paymentId);
                                  }}
                                  className="text-gray-400 hover:text-red-600 transition-colors"
                                  title={t('common.delete', 'Delete')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('drivers.record_payment', 'Record Payment')}
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label">{t('drivers.amount', 'Amount')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                  className="input-field"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="label">{t('drivers.payment_date', 'Payment date')}</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm(p => ({ ...p, paymentDate: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">{t('drivers.payment_method', 'Payment method')}</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm(p => ({ ...p, method: e.target.value }))}
                  className="input-field"
                >
                  <option value="cash">{t('drivers.method_cash', 'Cash')}</option>
                  <option value="bank_transfer">{t('drivers.method_bank_transfer', 'Bank transfer')}</option>
                  <option value="other">{t('drivers.method_other', 'Other')}</option>
                </select>
              </div>
              <div>
                <label className="label">{t('common.notes', 'Notes')}</label>
                <textarea
                  rows={2}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2 rtl:space-x-reverse">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="btn btn-ghost"
                disabled={savingPayment}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSavePayment}
                className="btn btn-primary"
                disabled={savingPayment}
              >
                {savingPayment ? '...' : t('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {showStatementModal && (() => {
        const getShipmentDateMs = (s) =>
          toMillis(s.pickupDate) || toMillis(s.shipmentDate) || toMillis(s.createdAt) || 0;
        const tripEntries = shipments.map(s => ({
          id: `trip-${s.id}`,
          shipmentId: s.id,
          type: 'trip',
          dateMs: getShipmentDateMs(s),
          createdMs: toMillis(s.createdAt),
          amount: Number(s.commissionAmount || 0),
          description: `${t('drivers.trip', 'Trip')} ${s.trackingNumber || s.shipmentNumber || s.id.slice(0, 6)}`,
          secondary: [getClientName(s), formatRouteDisplay(s).origin && formatRouteDisplay(s).destination ? `${formatRouteDisplay(s).origin} - ${formatRouteDisplay(s).destination}` : null].filter(Boolean).join(' | ')
        }));
        const paymentEntries = driverPayments.map(p => ({
          id: `pay-${p.id}`,
          paymentId: p.id,
          type: 'payment',
          dateMs: toMillis(p.paymentDate) || toMillis(p.createdAt),
          createdMs: toMillis(p.createdAt),
          amount: Number(p.amount) || 0,
          description: t('drivers.payment_received', 'Payment'),
          secondary: [p.method, p.notes].filter(Boolean).join(' | ')
        }));
        return (
          <DriverStatementModal
            isOpen={showStatementModal}
            onClose={() => setShowStatementModal(false)}
            driver={driver}
            company={currentCompany}
            tripEntries={tripEntries}
            paymentEntries={paymentEntries}
          />
        );
      })()}

      {/* Quick View Shipment Modal */}
      {quickViewShipment && (() => {
        const s = quickViewShipment;
        const route = formatRouteDisplay(s);
        const validCities = Array.isArray(s.routeCities)
          ? s.routeCities.filter(c => c && c.city)
          : [];
        const totalStops = getStopsCount(s);
        const vehicle = vehicles.find(v => v.id === s.vehicleId);
        const pickupMs = toMillis(s.pickupDate) || toMillis(s.shipmentDate) || toMillis(s.createdAt);
        return (
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setQuickViewShipment(null)}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 sticky top-0 bg-white">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {t('drivers.quick_view', 'Quick view')}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {s.trackingNumber || s.shipmentNumber || s.id.slice(0, 6)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setQuickViewShipment(null);
                      navigate(`/shipments/${s.id}`);
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    {t('drivers.view_full_shipment', 'View full shipment')}
                  </button>
                  <button
                    onClick={() => setQuickViewShipment(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{t('drivers.client', 'Client')}</p>
                    <p className="font-medium text-gray-900 mt-0.5">{getClientName(s)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{t('drivers.status', 'Status')}</p>
                    <p className="font-medium text-gray-900 mt-0.5">{s.status || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{t('drivers.pickup_date', 'Pickup date')}</p>
                    <p className="font-medium text-gray-900 mt-0.5">
                      {pickupMs ? new Date(pickupMs).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">{t('drivers.vehicle', 'Vehicle')}</p>
                    <p className="font-medium text-gray-900 mt-0.5">
                      {vehicle ? (vehicle.plateNumber || vehicle.name || '—') : '—'}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                    {t('drivers.route', 'Route')}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">
                      {route.origin} {routeArrow} {route.destination}
                    </span>
                    {totalStops > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                        {t('drivers.locations', { count: totalStops, defaultValue: '{{count}} locations' })}
                      </span>
                    )}
                  </div>
                </div>

                {validCities.length > 0 && (
                  <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
                    {validCities.map((c, idx) => {
                      const stopsArr = Array.isArray(c.stops) ? c.stops : [];
                      const cnt = Math.max(stopsArr.length, Number(c.stopCount) || 0);
                      return (
                        <div key={idx} className="px-3 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {idx + 1}. {getCityName(c.city)}
                              {c.site ? <span className="text-gray-500 font-normal"> — {c.site}</span> : null}
                            </span>
                            {cnt > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800">
                                {t('drivers.locations', { count: cnt, defaultValue: '{{count}} locations' })}
                              </span>
                            )}
                          </div>
                          {stopsArr.length > 0 && (
                            <ul className="mt-1 text-xs text-gray-600 space-y-0.5 ps-4 list-disc">
                              {stopsArr.map((stop, i) => {
                                const code = stop.code || stop.number || stop.branchNumber || stop.branchCode || '';
                                const nm = stop.name || stop.label || stop.value || stop.branchName || stop.site || stop.description || (typeof stop === 'string' ? stop : '');
                                return (
                                  <li key={i}>{[code, nm].filter(Boolean).join(' - ') || '—'}</li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {t('drivers.total_trip_amount', 'Total trip amount')}
                    </p>
                    <p className="font-semibold text-gray-900 mt-0.5">
                      {formatCurrency(Number(s.commissionAmount || 0))}
                    </p>
                  </div>
                  {s.weight && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">{t('drivers.weight', 'Weight')}</p>
                      <p className="font-medium text-gray-900 mt-0.5">{s.weight}</p>
                    </div>
                  )}
                </div>

                {s.notes && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-0.5">{t('drivers.notes', 'Notes')}</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{s.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit Driver Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h3 className="text-base font-semibold text-gray-900">
                {t('drivers.edit_driver', 'Edit Driver')}
              </h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); handleSaveDriver(); }}
              className="p-5 space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.driver_name')} *</label>
                  <input type="text" name="name" value={editForm.name} onChange={handleEditChange} className="input-field" required />
                </div>
                <div>
                  <label className="label">{t('drivers.nickname', 'Nickname')}</label>
                  <input type="text" name="nickname" value={editForm.nickname} onChange={handleEditChange} className="input-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.phone')} *</label>
                  <input type="tel" name="phone" value={editForm.phone} onChange={handleEditChange} className="input-field" required />
                </div>
                <div>
                  <label className="label">{t('drivers.email')}</label>
                  <input type="email" name="email" value={editForm.email} onChange={handleEditChange} className="input-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.license_number')}</label>
                  <input type="text" name="licenseNumber" value={editForm.licenseNumber} onChange={handleEditChange} className="input-field" />
                </div>
                <div>
                  <label className="label">{t('drivers.license_expiry')}</label>
                  <input type="date" name="licenseExpiry" value={editForm.licenseExpiry} onChange={handleEditChange} className="input-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.iqamah_number')}</label>
                  <input type="text" name="iqamahNumber" value={editForm.iqamahNumber} onChange={handleEditChange} className="input-field" />
                </div>
                <div>
                  <label className="label">{t('drivers.iqamah_expiry')}</label>
                  <input type="date" name="iqamahExpiry" value={editForm.iqamahExpiry} onChange={handleEditChange} className="input-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.health_insurance_number')}</label>
                  <input type="text" name="healthInsuranceNumber" value={editForm.healthInsuranceNumber} onChange={handleEditChange} className="input-field" />
                </div>
                <div>
                  <label className="label">{t('drivers.health_insurance_expiry')}</label>
                  <input type="date" name="healthInsuranceExpiry" value={editForm.healthInsuranceExpiry} onChange={handleEditChange} className="input-field" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.favorite_vehicle')}</label>
                  <select name="favoriteVehicleId" value={editForm.favoriteVehicleId} onChange={handleEditChange} className="input-field">
                    <option value="">{t('common.select_option', 'Select...')}</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.plateNumber} - {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('drivers.status')}</label>
                  <select name="status" value={editForm.status} onChange={handleEditChange} className="input-field">
                    <option value="active">{t('drivers.active')}</option>
                    <option value="inactive">{t('drivers.inactive')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">{t('drivers.notes')}</label>
                <textarea name="notes" value={editForm.notes} onChange={handleEditChange} className="input-field" rows={3} />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn-secondary" disabled={savingDriver}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary" disabled={savingDriver}>
                  {savingDriver ? '...' : t('common.save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default DriverDetail; 